/* Volatile — full-screen search overlay (Figma 187:1121).
 *
 * Tap the header search icon → overlay slides in from the right, search
 * input autofocuses, and live-results from `/search/suggest.json` populate
 * a 2-col (mobile) / 4-col (desktop) product grid below. Filters and Sort
 * open small bottom sheets; both apply to the cached result set client-
 * side so we never round-trip to a search page.
 *
 * Closing (back arrow / Esc / clear-x on empty input) restores the page
 * exactly where the user left it because the overlay is just a fixed
 * sibling — the underlying scroll position and component state are
 * never disturbed.
 */
(() => {
  const screen = document.querySelector('[data-search-screen]');
  const input = document.querySelector('[data-search-input]');
  const results = document.querySelector('[data-search-results]');
  const drawer = document.querySelector('[data-search-drawer]');
  if (!screen || !input || !results) return;

  /* ── Refine UI: single right-slide drawer with filter + sort tabs,
        used on every viewport. The mobile bottom sheets were removed —
        the floating refine FAB at the bottom-right of the overlay
        opens this drawer on every device. ─── */
  const openDrawer = (tab) => {
    if (!drawer || typeof drawer.showModal !== 'function') return;
    if (!drawer.open) drawer.showModal();
    if (tab) switchDrawerTab(tab);
  };
  const closeDrawer = () => {
    if (drawer && drawer.open) drawer.close();
  };
  const switchDrawerTab = (which) => {
    if (!drawer) return;
    drawer.querySelectorAll('[data-search-tab]').forEach((btn) => {
      const active = btn.dataset.searchTab === which;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    drawer.querySelectorAll('[data-search-panel]').forEach((panel) => {
      const active = panel.dataset.searchPanel === which;
      panel.classList.toggle('is-active', active);
      if (active) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    });
  };

  const STATE_ATTR = 'data-search-open';

  /* ── Open / close ─────────────────────────────────────────────────── */
  // Stash the scrollY before pinning the body. Body becomes
  // `position: fixed` (via critical.css) which collapses scrollY to 0;
  // we offset its `top` so the page LOOKS pinned in place, then restore
  // the real scrollY on close. iOS Safari needs this to fully prevent
  // the page from scrolling underneath the overlay.
  let savedScrollY = 0;
  const open = () => {
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = `-${savedScrollY}px`;
    document.body.setAttribute(STATE_ATTR, 'true');
    screen.setAttribute('aria-hidden', 'false');
    // Tiny delay so iOS Safari respects the focus call after the slide
    // animation kicks off — without it the on-screen keyboard sometimes
    // flashes shut.
    setTimeout(() => input.focus({ preventScroll: true }), 80);
  };
  const close = () => {
    document.body.removeAttribute(STATE_ATTR);
    document.body.style.top = '';
    window.scrollTo(0, savedScrollY);
    screen.setAttribute('aria-hidden', 'true');
    input.blur();
    closeDrawer();
  };

  /* ── Render helpers ───────────────────────────────────────────────── */
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));

  const moneyINR = (cents) => {
    const n = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 })
      .format(Math.round(cents / 100));
    return n;
  };

  // Convert raw price (cents int OR rendered string) → "2,499" without the
  // currency prefix. We render INR / 2,499 / ¥ separately to match Figma.
  const priceNumOnly = (raw) => {
    if (raw == null) return '';
    if (typeof raw === 'number') return moneyINR(raw);
    let s = String(raw).replace(/<[^>]+>/g, '').trim();
    s = s.replace(/\.00\b/g, '')
         .replace(/^INR\s*/i, '')
         .replace(/^Rs\.?\s*/i, '')
         .replace(/^[₹$€£]\s*/, '');
    const n = Number(s.replace(/[^\d.]/g, ''));
    return (isFinite(n) && n > 0)
      ? new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 })
          .format(Math.round(n))
      : s;
  };

  // Parse a price into cents (number) for client-side sorting / filtering.
  const priceToCents = (raw) => {
    if (raw == null) return 0;
    if (typeof raw === 'number') return Math.round(raw);
    let s = String(raw).replace(/<[^>]+>/g, '').trim()
      .replace(/^INR\s*/i, '').replace(/^Rs\.?\s*/i, '').replace(/^[₹$€£]\s*/, '');
    const n = Number(s.replace(/[^\d.]/g, ''));
    return isFinite(n) ? Math.round(n * 100) : 0;
  };

  // Approx INR → JPY converter (matches the v-price snippet's 1.85×2 path
  // — we don't have access to settings.jpy_rate from JS, so use a
  // reasonable default. Refine later if needed.)
  const yenFromInrCents = (cents) => {
    const inr = cents / 100;
    return Math.round(inr * 1.85 * 2);
  };

  /* ── Image URL helper — `/products/<handle>.js` returns image URLs
     as plain strings (no width param); `/search/suggest.json` returns
     them as objects with `.url`. Coerce to a plain URL string and
     append a Shopify-CDN width param for responsive loading. ─── */
  const imgUrl = (raw, width) => {
    if (!raw) return '';
    let u = typeof raw === 'string' ? raw : (raw.url || '');
    if (!u) return '';
    // Shopify CDN URLs accept ?width=N — preserve any existing query.
    const sep = u.indexOf('?') === -1 ? '?' : '&';
    return `${u}${sep}width=${width}`;
  };

  /* ── Card render — the SAME multi-image markup as
     `snippets/product-card.liquid`. Used to be single-image only
     (suggest.json returns just `featured_image`); now we hydrate
     each result with `/products/<handle>.js` first so we have the
     full `images` array. Cards on the search overlay are now
     visually identical to the home Featured grid + PDP recs (same
     hover-stack swap, same mobile horizontal scroll-snap, same
     slider thumb + glass nav arrows). ── */
  const cardHTML = (p, index) => {
    const cents = priceToCents(p.price);
    const compareCents = priceToCents(p.compare_at_price);
    const onSale = compareCents > cents && cents > 0;
    const num = priceNumOnly(p.price);
    const yen = yenFromInrCents(cents);
    const compareNum = priceNumOnly(p.compare_at_price);

    // Normalize the image array. /products/<handle>.js returns plain URL
    // strings; /search/suggest.json returns featured_image as an object.
    const rawImages = Array.isArray(p.images) && p.images.length > 0
      ? p.images
      : (p.featured_image ? [p.featured_image] : []);
    const imageCount = rawImages.length;

    // Aspect ratio — prefer suggest.json's featured_image.aspect_ratio if
    // present, fall back to product-card default 0.8 (matches snippet).
    const ratio = (p.featured_image
                   && typeof p.featured_image === 'object'
                   && typeof p.featured_image.aspect_ratio === 'number'
                   && p.featured_image.aspect_ratio > 0)
      ? p.featured_image.aspect_ratio
      : 0.8;

    // Mirror the snippet's `is_reversed` rule: every alternate card
    // (odd index) shows its second image as the primary on hover.
    const isReversed = (typeof index === 'number') && (index % 2 === 1);
    const primaryRaw = (isReversed && imageCount > 1) ? rawImages[1] : rawImages[0];
    const hoverRaw   = (isReversed && imageCount > 1) ? rawImages[0] : rawImages[1];

    const primaryImg = primaryRaw
      ? `<img class="v-card__img v-card__img--primary" src="${esc(imgUrl(primaryRaw, 1200))}" alt="${esc(p.title)}" loading="lazy">`
      : '<div class="v-card__placeholder" aria-hidden="true"></div>';
    const hoverImg = (hoverRaw && hoverRaw !== primaryRaw)
      ? `<img class="v-card__img v-card__img--hover" src="${esc(imgUrl(hoverRaw, 1200))}" alt="${esc(p.title)}" loading="lazy">`
      : '';

    // Mobile scroll carousel — every product image, in order. Reversed
    // cards put image[1] first, then image[0], then the rest in order
    // (mirrors snippet/product-card.liquid).
    let scrollImgs = '';
    if (imageCount === 0) {
      scrollImgs = '<div class="v-card__placeholder" aria-hidden="true"></div>';
    } else if (isReversed && imageCount >= 2) {
      const ordered = [rawImages[1], rawImages[0], ...rawImages.slice(2)];
      scrollImgs = ordered.map((src) =>
        `<img class="v-card__scroll-img" src="${esc(imgUrl(src, 1200))}" alt="${esc(p.title)}" loading="lazy">`
      ).join('');
    } else {
      scrollImgs = rawImages.map((src) =>
        `<img class="v-card__scroll-img" src="${esc(imgUrl(src, 1200))}" alt="${esc(p.title)}" loading="lazy">`
      ).join('');
    }

    const tag = onSale
      ? '<span class="v-card__tag">Sale</span>'
      : (p.available === false ? '<span class="v-card__tag v-card__tag--muted">Sold out</span>' : '');

    // Slider thumb + hover nav arrows — only when there's >1 image
    // (matches the snippet's `images.size > 1` gate).
    const slider = imageCount > 1
      ? `<div class="v-card__progress" data-card-dots data-image-count="${imageCount}" aria-hidden="true">
           <span class="v-card__progress-thumb"></span>
         </div>
         <span class="v-card__nav v-card__nav--prev" role="button" tabindex="-1" data-card-nav="prev" aria-hidden="true">
           <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 6 8 12l6 6"/></svg>
         </span>
         <span class="v-card__nav v-card__nav--next" role="button" tabindex="-1" data-card-nav="next" aria-hidden="true">
           <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 6l6 6-6 6"/></svg>
         </span>`
      : '';

    const priceInner = onSale
      ? `<s class="v-price__compare"><span class="v-price__cur">INR</span><span class="v-price__num">${esc(compareNum)}</span></s>` +
        `<span class="v-price__current"><span class="v-price__cur">INR</span><span class="v-price__num">${esc(num)}</span></span>` +
        `<span class="v-price__jp" data-no-translate>/ ¥ ${esc(String(yen))}</span>`
      : `<span class="v-price__current"><span class="v-price__cur">INR</span><span class="v-price__num">${esc(num)}</span></span>` +
        `<span class="v-price__jp" data-no-translate>/ ¥ ${esc(String(yen))}</span>`;

    return `
      <a class="v-card" href="${esc(p.url)}" aria-label="${esc(p.title)}">
        <div class="v-card__media" style="aspect-ratio: ${ratio};">
          <div class="v-card__hover-stack" aria-hidden="true">${primaryImg}${hoverImg}</div>
          <div class="v-card__scroll" data-card-scroll>${scrollImgs}</div>
          ${tag}
          ${slider}
        </div>
        <div class="v-card__meta">
          <span class="v-card__title">${esc(p.title)}</span>
          <span class="v-card__title-jp" lang="ja" data-jp-mirror>${esc(p.title)}</span>
          <span class="v-card__price"><span class="v-price">${priceInner}</span></span>
        </div>
      </a>`;
  };

  const renderHint = (text) => {
    results.innerHTML = `<p class="v-search-screen__hint">${esc(text)}</p>`;
  };
  const renderEmpty = (q) => {
    results.innerHTML = `<p class="v-search-screen__empty">No results for &ldquo;${esc(q)}&rdquo;.</p>`;
  };
  const renderGrid = (products) => {
    if (!products.length) {
      results.innerHTML = '<p class="v-search-screen__hint">No matches.</p>';
      return;
    }
    // Same `.v-grid` shared component used by featured, catalog, related.
    // `data-cols="4"` matches the home-featured grid (set in
    // templates/index.json) so cards on every surface — home, search,
    // catalog, PDP recs — are the EXACT same width / shape. Pass index
    // so the alternate-image-swap rule from product-card.liquid applies
    // here too.
    results.innerHTML =
      `<div class="v-grid v-search-screen__grid" data-cols="4">${
        products.map((p, i) => cardHTML(p, i)).join('')
      }</div>`;
    // The card-carousel.js MutationObserver auto-binds slider thumb +
    // hover-arrow navigation on the new `[data-card-scroll]` nodes.
  };

  /* ── Filter / sort state ──────────────────────────────────────────── */
  let cachedProducts = [];
  let activeFilters = new Set();
  let activeSort = 'relevance';

  const applyFiltersAndSort = () => {
    let list = cachedProducts.slice();
    if (activeFilters.has('available')) {
      list = list.filter((p) => p.available !== false);
    }
    if (activeFilters.has('sale')) {
      list = list.filter((p) => priceToCents(p.compare_at_price) > priceToCents(p.price));
    }
    switch (activeSort) {
      case 'price-asc':  list.sort((a, b) => priceToCents(a.price) - priceToCents(b.price)); break;
      case 'price-desc': list.sort((a, b) => priceToCents(b.price) - priceToCents(a.price)); break;
      case 'title-asc':  list.sort((a, b) => String(a.title).localeCompare(String(b.title))); break;
      // 'relevance' = whatever order suggest.json returned (default)
      default: break;
    }
    renderGrid(list);
  };

  /* ── Live search ──────────────────────────────────────────────────── */
  let debounceId;
  let lastQuery = '';
  // Cache hydrated products by handle so repeat queries don't refetch.
  const productCache = new Map();

  // Pull the full product (all images, variants) from `/products/<handle>.js`
  // so the search overlay can render the EXACT same card markup the home
  // Featured grid + PDP recs render. Single-image products skip the fetch
  // since the suggest.json result already has everything needed.
  const hydrate = async (stub) => {
    if (!stub.handle) return stub;
    if (productCache.has(stub.handle)) return productCache.get(stub.handle);
    try {
      const r = await fetch(`/products/${stub.handle}.js`,
        { headers: { 'Accept': 'application/json' } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const full = await r.json();
      // Merge: keep suggest.json's featured_image (object form with
      // aspect_ratio) but adopt full.images (array of all URLs).
      const merged = {
        ...stub,
        ...full,
        // suggest.json has a richer featured_image object (with
        // aspect_ratio + alt) — preserve it.
        featured_image: stub.featured_image || full.featured_image,
        // /products/<handle>.js gives us all images.
        images: Array.isArray(full.images) && full.images.length > 0
          ? full.images
          : (stub.featured_image ? [stub.featured_image] : []),
        // Preserve the suggest.json url (clean PDP URL).
        url: stub.url || full.url || `/products/${stub.handle}`,
      };
      productCache.set(stub.handle, merged);
      return merged;
    } catch (err) {
      productCache.set(stub.handle, stub);
      return stub;
    }
  };

  const fetchAndRender = async (q) => {
    const trimmed = q.trim();
    if (trimmed.length < 1) {
      cachedProducts = [];
      renderHint('Start typing to search.');
      return;
    }
    if (trimmed.length < 2) {
      // Single character — show the placeholder rather than firing a
      // mostly-useless query.
      renderHint('Keep typing…');
      return;
    }
    lastQuery = trimmed;
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(trimmed)}` +
        `&resources[type]=product&resources[limit]=20`;
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // If the user kept typing, ignore stale responses.
      if (trimmed !== lastQuery) return;
      const stubs = (data.resources && data.resources.results
                        && data.resources.results.products) || [];
      if (!stubs.length) { cachedProducts = []; renderEmpty(trimmed); return; }
      // Hydrate every stub in parallel via /products/<handle>.js so the
      // rendered cards have all images + variants, matching the home
      // Featured grid byte-for-byte.
      const hydrated = await Promise.all(stubs.map(hydrate));
      // Bail if a newer query landed in the meantime.
      if (trimmed !== lastQuery) return;
      cachedProducts = hydrated;
      applyFiltersAndSort();
    } catch (err) {
      renderHint('Something went wrong. Try again.');
    }
  };

  input.addEventListener('input', () => {
    clearTimeout(debounceId);
    const v = input.value;
    debounceId = setTimeout(() => fetchAndRender(v), 220);
  });

  // Pressing Enter on the input still navigates to the full search page
  // (form's default action) for accessibility — users who prefer the
  // dedicated search results page get it. The overlay close happens via
  // the submit-then-navigate, no JS preventDefault needed.

  /* ── Click / key handling for open / close / sheets ───────────────── */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-search]')) {
      e.preventDefault();
      open();
      return;
    }
    if (e.target.closest('[data-close-search]')) {
      e.preventDefault();
      close();
      return;
    }
    if (e.target.closest('[data-search-clear]')) {
      e.preventDefault();
      if (input.value.length > 0) {
        input.value = '';
        cachedProducts = [];
        renderHint('Start typing to search.');
        input.focus();
      } else {
        close();
      }
      return;
    }
    if (e.target.closest('[data-search-refine]')) {
      e.preventDefault();
      // Refine FAB — open the side drawer on the filter tab by default.
      openDrawer('filter');
      return;
    }
    const tabBtn = e.target.closest('[data-search-tab]');
    if (tabBtn) {
      e.preventDefault();
      switchDrawerTab(tabBtn.dataset.searchTab);
      return;
    }
    if (e.target.closest('[data-close-search-drawer]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    if (e.target.closest('[data-search-drawer-clear]')) {
      e.preventDefault();
      resetRefine();
      return;
    }
  });

  /* Reset every filter / sort input across both the desktop drawer and the
     two mobile sheets so the visible UI matches the cleared state. */
  const resetRefine = () => {
    document.querySelectorAll('[data-search-filter-input]').forEach((cb) => { cb.checked = false; });
    document.querySelectorAll('[data-search-sort-input]').forEach((rb) => { rb.checked = rb.value === 'relevance'; });
    activeFilters = new Set();
    activeSort = 'relevance';
    applyFiltersAndSort();
  };

  // Filter checkboxes — toggle activeFilters, re-render.
  document.querySelectorAll('[data-search-filter-input]').forEach((cb) => {
    cb.addEventListener('change', () => {
      const v = cb.value;
      if (cb.checked) activeFilters.add(v);
      else activeFilters.delete(v);
      applyFiltersAndSort();
    });
  });
  // Sort radios — set activeSort, re-render.
  document.querySelectorAll('[data-search-sort-input]').forEach((rb) => {
    rb.addEventListener('change', () => {
      if (rb.checked) {
        activeSort = rb.value;
        applyFiltersAndSort();
      }
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (drawer && drawer.open) { drawer.close(); return; }
    if (document.body.getAttribute(STATE_ATTR) === 'true') close();
  });

  // Backdrop tap closes the side drawer.
  if (drawer) {
    drawer.addEventListener('click', (e) => { if (e.target === drawer) drawer.close(); });
  }
})();
